import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
} from "mdb-react-ui-kit";
import {
  buildDashboardOrderPlatforms,
  canonicalPlatformId,
  findMatchingPlatform,
} from "../utils/dashboardPlatforms";

/**
 * Figma 1:2688 Orders by Status
 * Stacked contiguous segments; row fill scales to max status total.
 * Platforms come from the catalog + ordersByDeliveryApps (staging).
 */
const STATUS_FIELDS = [
  { status: "Completed", keys: ["completed", "COMPLETED"] },
  { status: "Cancelled", keys: ["cancelled", "CANCELLED"] },
  { status: "Time Out", keys: ["timeOut", "TIME_OUT", "timeout", "time_out"] },
  { status: "Rejected", keys: ["rejected", "REJECTED"] },
];

/** Preferred L→R stack when present; other platforms append after. */
const STACK_PRIORITY = [
  "THE CHEFZ",
  "JAHEZ",
  "KEETA",
  "NINJA",
  "CHAT_AGENT",
  "DIGITAL_CATALOG",
];

/** Legend order when present; other platforms append after. */
const LEGEND_PRIORITY = [
  "KEETA",
  "THE CHEFZ",
  "JAHEZ",
  "NINJA",
  "CHAT_AGENT",
  "DIGITAL_CATALOG",
];

const readCount = (item, keys) => {
  for (const key of keys) {
    if (item?.[key] != null) return Number(item[key]) || 0;
  }
  return 0;
};

const priorityIndex = (platform, priority) => {
  const id = canonicalPlatformId(platform?.id || platform?.key);
  const idx = priority.findIndex((pref) => {
    const prefId = canonicalPlatformId(pref);
    return id === prefId || id.includes(prefId) || prefId.includes(id);
  });
  return idx === -1 ? priority.length : idx;
};

const sortByPriority = (platforms, priority) =>
  [...platforms].sort(
    (a, b) => priorityIndex(a, priority) - priorityIndex(b, priority)
  );

const OrdersCard = ({ data, platforms: platformCatalog = [] }) => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";

  const apps = useMemo(() => {
    if (Array.isArray(data?.ordersByDeliveryApps)) return data.ordersByDeliveryApps;
    if (Array.isArray(data?.data?.ordersByDeliveryApps)) {
      return data.data.ordersByDeliveryApps;
    }
    return [];
  }, [data]);

  const platforms = useMemo(
    () => buildDashboardOrderPlatforms(platformCatalog, apps, isArabic),
    [platformCatalog, apps, isArabic]
  );

  const stackPlatforms = useMemo(
    () => sortByPriority(platforms, STACK_PRIORITY),
    [platforms]
  );

  const legendPlatforms = useMemo(
    () => sortByPriority(platforms, LEGEND_PRIORITY),
    [platforms]
  );

  const breakdown = useMemo(() => {
    const empty = () =>
      platforms.reduce((acc, plat) => {
        acc[plat.id] = 0;
        return acc;
      }, {});

    const b = {
      Completed: empty(),
      Cancelled: empty(),
      "Time Out": empty(),
      Rejected: empty(),
    };

    apps.forEach((item) => {
      const match = findMatchingPlatform(platforms, item?._id);
      if (!match) return;
      STATUS_FIELDS.forEach(({ status, keys }) => {
        b[status][match.id] = readCount(item, keys);
      });
    });
    return b;
  }, [apps, platforms]);

  const ordersData = useMemo(
    () =>
      STATUS_FIELDS.map(({ status }) => {
        const counts = breakdown[status] || {};
        const total = platforms.reduce(
          (sum, plat) => sum + (Number(counts[plat.id]) || 0),
          0
        );
        return { status, total, breakdown: counts };
      }),
    [breakdown, platforms]
  );

  const maxTotal = useMemo(
    () => Math.max(1, ...ordersData.map((item) => item.total)),
    [ordersData]
  );

  const statusLabel = (status) => {
    switch (status) {
      case "Completed":
        return isArabic ? "مكتمل" : "Completed";
      case "Cancelled":
        return (
          t("ordersPage.problemOrderCard.cancelled") ||
          t("ordersPage.filtertabs.cancelled") ||
          "Cancelled"
        );
      case "Time Out":
        return (
          t("ordersPage.problemOrderCard.timeout") ||
          t("ordersPage.filtertabs.timeout") ||
          "Time Out"
        );
      case "Rejected":
        return (
          t("ordersPage.problemOrderCard.rejected") ||
          t("ordersPage.filtertabs.rejected") ||
          "Rejected"
        );
      default:
        return status;
    }
  };

  /**
   * Contiguous stacked fill (screenshot):
   * brand segments | gray remainder — no gaps, pill ends via track clip.
   */
  const getStackedBars = (item) => {
    if (!item.total) return [];

    const fillPct = (item.total / maxTotal) * 100;
    let cursor = 0;
    const segments = [];

    stackPlatforms.forEach((plat) => {
      const count = item.breakdown[plat.id] || 0;
      if (!count) return;
      const widthPct = (count / item.total) * fillPct;
      segments.push({
        id: plat.id,
        label: plat.label,
        count,
        color: plat.color,
        leftPct: cursor,
        widthPct,
      });
      cursor += widthPct;
    });

    // Absorb float error so segments stay flush
    if (segments.length) {
      const sumW = segments.reduce((s, seg) => s + seg.widthPct, 0);
      segments[segments.length - 1].widthPct += fillPct - sumW;
    }

    return segments.map((seg, index) => ({
      ...seg,
      isFirst: index === 0,
      isLast: index === segments.length - 1,
    }));
  };

  return (
    <MDBCard
      className="oh-dashboard-card oh-dashboard-card--orders h-100 shadow-0 border mb-0"
      dir={isArabic ? "rtl" : undefined}
    >
      <MDBCardBody className="oh-dashboard-orders">
        <MDBCardTitle className="oh-dashboard-card__title oh-dashboard-orders__title">
          {t("dashboardPage.orders")}
        </MDBCardTitle>

        <div className="oh-dashboard-card__list">
          {ordersData.map((item) => {
            const bars = getStackedBars(item);
            return (
              <div key={item.status} className="oh-dashboard-card__status">
                <div className="oh-dashboard-card__status-head">
                  <span className="oh-dashboard-card__status-label">
                    {statusLabel(item.status)}
                  </span>
                  <span className="oh-dashboard-card__status-count">
                    {item.total}
                  </span>
                </div>

                <div
                  className="oh-dashboard-card__track oh-dashboard-card__track--stack"
                  role="img"
                  aria-label={statusLabel(item.status)}
                >
                  {bars.map((bar) => (
                    <div
                      key={bar.id}
                      className={[
                        "oh-dashboard-card__bar",
                        "oh-dashboard-card__bar--stack",
                        bar.isFirst ? "oh-dashboard-card__bar--start" : "",
                        bar.isLast ? "oh-dashboard-card__bar--end" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{
                        insetInlineStart: `${bar.leftPct}%`,
                        width: `${bar.widthPct}%`,
                        backgroundColor: bar.color,
                      }}
                      title={`${bar.label}: ${bar.count}`}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="oh-dashboard-card__legend oh-dashboard-card__legend--orders">
          {legendPlatforms.map((plat) => (
            <div key={plat.id} className="oh-dashboard-card__legend-item">
              <span
                className="oh-dashboard-card__legend-dot"
                style={{ backgroundColor: plat.color }}
              />
              <span className="oh-dashboard-card__legend-label">
                {plat.label}
              </span>
            </div>
          ))}
        </div>
      </MDBCardBody>
    </MDBCard>
  );
};

export default OrdersCard;
